import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Button,
  Label,
  Icon,
  Loader,
  Message,
  Form,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi, getAuthHeaders } from '../../api';

const TYPE_OPTIONS = [
  { key: 'REC', value: 'REC', text: 'Record' },
  { key: 'POL', value: 'POL', text: 'Policy' },
  { key: 'SOP', value: 'SOP', text: 'SOP' },
  { key: 'WI', value: 'WI', text: 'Work Instruction' },
  { key: 'FRM', value: 'FRM', text: 'Form / Template' },
  { key: 'TECH', value: 'TECH', text: 'Technical Document' },
];

const DOMAIN_OPTIONS = [
  { key: 'DC', value: 'DC', text: 'Document Control' },
  { key: 'QMS', value: 'QMS', text: 'Quality Management' },
  { key: 'RM', value: 'RM', text: 'Risk Management' },
  { key: 'SW', value: 'SW', text: 'Software' },
  { key: 'CM', value: 'CM', text: 'Change Management' },
  { key: 'CS', value: 'CS', text: 'Cybersecurity' },
  { key: 'UE', value: 'UE', text: 'Usability Engineering' },
  { key: 'TR', value: 'TR', text: 'Translation' },
  { key: 'REG', value: 'REG', text: 'Regulatory' },
  { key: 'SUP', value: 'SUP', text: 'Supplier / Procurement' },
  { key: 'HR', value: 'HR', text: 'Human Resources' },
];

const CONFIDENCE_COLORS = {
  high: 'green',
  medium: 'yellow',
  low: 'orange',
};

export default function UnregisteredDocuments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState({});
  const [batchRegistering, setBatchRegistering] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [successMsg, setSuccessMsg] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchApi(`${getApiPath()}/@qms/register/unregistered`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const getOverride = (docId, field, fallback) => {
    return overrides[docId]?.[field] ?? fallback;
  };

  const setOverride = (docId, field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [docId]: { ...(prev[docId] || {}), [field]: value },
    }));
  };

  const registerOne = async (item) => {
    setRegistering((prev) => ({ ...prev, [item.document_id]: true }));
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await fetch(`${getApiPath()}/@qms/register/from-kb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          docs_id: item.docs_id,
          document_id: item.document_id,
          title: item.kb_title,
          document_type: getOverride(item.document_id, 'type', item.suggested_type),
          domain_code: getOverride(item.document_id, 'domain', item.suggested_domain),
          version: item.suggested_version,
          status: item.suggested_status,
          classification: item.suggested_classification,
        }),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (res.status === 403) {
        setError('Permission denied. You need the Manage Documents permission.');
        setRegistering((prev) => ({ ...prev, [item.document_id]: false }));
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      setSuccessMsg(`Registered ${item.document_id}`);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistering((prev) => ({ ...prev, [item.document_id]: false }));
    }
  };

  const registerAll = async () => {
    if (!data?.items?.length) return;
    setBatchRegistering(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const batchItems = data.items.map((item) => ({
        docs_id: item.docs_id,
        document_id: item.document_id,
        title: item.kb_title,
        document_type: getOverride(item.document_id, 'type', item.suggested_type),
        domain_code: getOverride(item.document_id, 'domain', item.suggested_domain),
        version: item.suggested_version,
        status: item.suggested_status,
        classification: item.suggested_classification,
      }));

      const res = await fetch(`${getApiPath()}/@qms/register/from-kb/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ items: batchItems }),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (res.status === 403) {
        setError('Permission denied. You need the Manage Documents permission.');
        setBatchRegistering(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      const result = await res.json();
      setSuccessMsg(
        `Registered ${result.registered_count} document(s)` +
          (result.error_count > 0
            ? `. ${result.error_count} error(s).`
            : '.'),
      );
      if (result.error_count > 0) {
        setError(
          result.errors.map((e) => `${e.document_id}: ${e.error}`).join('; '),
        );
      }
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBatchRegistering(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading..." />
      </Container>
    );
  }

  const items = data?.items || [];

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="Unregistered Documents" />
      <Link to="/qms">&larr; QMS Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>Unregistered Documents</h1>
        <p className="subtitle">
          KB documents matching document ID patterns not yet in the register
        </p>
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {successMsg && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>{successMsg}</p>
        </Message>
      )}

      {items.length === 0 ? (
        <Segment placeholder textAlign="center">
          <Header icon>
            <Icon name="check circle" color="green" />
            All documents are registered
          </Header>
          <p>
            No Knowledge Base documents with document ID patterns are missing
            from the controlled document register.
          </p>
          <Segment.Inline>
            <Label size="large">
              Registered: {data?.registered_count || 0}
            </Label>
            <Label size="large">
              Excluded: {data?.excluded_count || 0}
            </Label>
          </Segment.Inline>
        </Segment>
      ) : (
        <>
          <Segment>
            <Label.Group>
              <Label color="red" size="large">
                <Icon name="warning sign" />
                {items.length} Unregistered
              </Label>
              <Label size="large">
                Registered: {data?.registered_count || 0}
              </Label>
              {data?.high_confidence_count > 0 && (
                <Label color="green" size="large">
                  {data.high_confidence_count} High Confidence
                </Label>
              )}
            </Label.Group>

            <Button
              primary
              loading={batchRegistering}
              disabled={batchRegistering}
              onClick={registerAll}
              style={{ marginTop: '0.5em' }}
            >
              <Icon name="check" /> Register All ({items.length})
            </Button>
          </Segment>

          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Document ID</Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Domain</Table.HeaderCell>
                <Table.HeaderCell>Confidence</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.document_id}>
                  <Table.Cell>
                    <strong>{item.document_id}</strong>
                    <br />
                    <span style={{ fontSize: '0.85em', color: '#888' }}>
                      {item.file_path}
                    </span>
                  </Table.Cell>
                  <Table.Cell>{item.kb_title}</Table.Cell>
                  <Table.Cell>
                    <Form.Select
                      compact
                      value={getOverride(
                        item.document_id,
                        'type',
                        item.suggested_type,
                      )}
                      onChange={(e, { value }) =>
                        setOverride(item.document_id, 'type', value)
                      }
                      options={TYPE_OPTIONS}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Form.Select
                      compact
                      value={getOverride(
                        item.document_id,
                        'domain',
                        item.suggested_domain,
                      )}
                      onChange={(e, { value }) =>
                        setOverride(item.document_id, 'domain', value)
                      }
                      options={DOMAIN_OPTIONS}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Label
                      color={CONFIDENCE_COLORS[item.confidence] || 'grey'}
                      size="small"
                    >
                      {item.confidence}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      primary
                      size="small"
                      loading={registering[item.document_id]}
                      disabled={registering[item.document_id]}
                      onClick={() => registerOne(item)}
                    >
                      <Icon name="check" /> Register
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </Container>
  );
}
